import React, { useState } from 'react'
import { Box, TextField, Button, CircularProgress } from '@mui/material'
import { isValidSpreadsheetUrl } from '../../utils'

export interface SpreadsheetInputProps {
  /** Callback when user submits a valid spreadsheet URL/ID */
  onLoad: (url: string) => void
  /** Whether the component is in a loading state */
  loading?: boolean
  /** Whether the component is disabled */
  disabled?: boolean
  /** Custom label for the input field */
  label?: string
  /** Custom placeholder text */
  placeholder?: string
  /** Custom helper text when valid */
  helperText?: string
  /** Custom error message when invalid */
  errorText?: string
  /** Custom button label */
  buttonLabel?: string
  /** Initial value for the input */
  initialValue?: string
}

export const SpreadsheetInput: React.FC<SpreadsheetInputProps> = ({
  onLoad,
  loading = false,
  disabled = false,
  label = 'Google Sheets URL',
  placeholder = 'https://docs.google.com/spreadsheets/d/...',
  helperText = 'Paste your Google Sheets URL or spreadsheet ID',
  errorText = 'Please enter a valid Google Sheets URL or spreadsheet ID',
  buttonLabel = 'Load',
  initialValue = '',
}) => {
  const [url, setUrl] = useState(initialValue)
  const [touched, setTouched] = useState(false)

  const isValid = isValidSpreadsheetUrl(url)
  const showError = touched && !isValid && url.length > 0

  const handleLoad = () => {
    if (isValid) {
      onLoad(url)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValid && !loading && !disabled) {
      handleLoad()
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <TextField
        fullWidth
        label={label}
        placeholder={placeholder}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onBlur={() => setTouched(true)}
        onKeyPress={handleKeyPress}
        error={showError}
        helperText={showError ? errorText : helperText}
        disabled={loading || disabled}
        variant="outlined"
      />
      <Button
        variant="contained"
        color="primary"
        onClick={handleLoad}
        disabled={!isValid || loading || disabled}
        sx={{ minWidth: 120, height: 56 }}
      >
        {loading ? <CircularProgress size={24} /> : buttonLabel}
      </Button>
    </Box>
  )
}
